import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const KEY = 'hg_currency';
const EURO_TZ = ['Berlin', 'Paris', 'Madrid', 'Rome', 'Amsterdam', 'Brussels', 'Vienna', 'Dublin', 'Lisbon', 'Helsinki', 'Athens',
  'Luxembourg', 'Bratislava', 'Ljubljana', 'Tallinn', 'Riga', 'Vilnius', 'Valletta', 'Nicosia', 'Zagreb', 'Monaco', 'Andorra', 'San_Marino', 'Vatican'];
const EURO_LANG = ['DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'IE', 'PT', 'FI', 'GR', 'LU', 'SK', 'SI', 'EE', 'LV', 'LT', 'MT', 'CY', 'HR'];

const detect = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('Australia/')) return 'AUD';
    if (tz === 'Europe/London' || tz === 'Europe/Belfast') return 'GBP';
    if (tz.startsWith('Europe/') && EURO_TZ.includes(tz.split('/')[1])) return 'EUR';
    const region = (navigator.language || '').split('-')[1]?.toUpperCase();
    if (region === 'AU') return 'AUD';
    if (region === 'GB') return 'GBP';
    if (EURO_LANG.includes(region)) return 'EUR';
  } catch { /* ignore */ }
  return 'USD';
};

const CurrencyCtx = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [info, setInfo] = useState(null);
  const [code, setCodeState] = useState(() => localStorage.getItem(KEY) || detect());

  const load = useCallback(() => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/currency`)
    .then(({ data }) => setInfo(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setCode = (c) => { localStorage.setItem(KEY, c); setCodeState(c); };
  const cur = info?.currencies?.find(c => c.code === code) || { code: 'USD', symbol: '$', rate: 1 };

  const value = useMemo(() => {
    const isUSD = cur.code === 'USD' || cur.rate === 1;
    const conv = (usd) => (isUSD ? Math.round(Number(usd) * 100) / 100 : Math.round(Number(usd) * cur.rate));
    const convExact = (usd) => (isUSD ? Number(usd) : Math.round(Number(usd) * cur.rate * 100) / 100);
    const fmtLocal = (amt, sym = cur.symbol) => `${sym}${Number(amt).toFixed(2)}`;
    const fmt = (usd) => fmtLocal(conv(usd));
    const totals = (items, shippingUsd = 0, discountUsd = 0) => {
      const subtotal = items.reduce((a, i) => a + conv(i.price) * Number(i.qty), 0);
      const shipping = shippingUsd ? conv(shippingUsd) : 0;
      const discount = Math.min(convExact(discountUsd || 0), subtotal);
      return { subtotal, shipping, discount, total: Math.round((subtotal - discount + shipping) * 100) / 100 };
    };
    return {
      code: cur.code, symbol: cur.symbol, rate: cur.rate, isUSD, setCode, reload: load,
      currencies: info?.currencies || [{ code: 'USD', symbol: '$', rate: 1 }],
      conv, convExact, fmt, fmtLocal, totals, ratesDate: info?.date,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur.code, cur.rate, cur.symbol, info, load]);

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
};

export const useCurrency = () => useContext(CurrencyCtx);

export const SYMBOLS = { USD: '$', AUD: 'A$', GBP: '£', EUR: '€' };
